import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AppShell, DropdownMenu, IconButton, Navigation, NavigationGroup, NavigationItem, NavigationProductSwitcher, WorkspaceBadge } from '@humanforce/mango-ui-react';
import { authService } from '../../auth/service';
import { HOME_PATH, itemPath, navSections } from '../../navigation/navConfig';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const isHomeActive = path === HOME_PATH;
  const isItemActive = (target: string) => path === target || path.startsWith(`${target}/`);
  const handleSignOut = () => {
    authService.logout({ redirect: false });
    navigate('/login', { replace: true });
  };

  const sidebar = (
    <Navigation tone="red" productSwitcher={<NavigationProductSwitcher icon="brand-payroll">Payroll</NavigationProductSwitcher>}>
      {navSections.map((section) => {
        // Home is the only direct destination; every other section is a non-navigating group.
        if (section.home)
          return (
            <NavigationItem key="home" prefix={section.icon} active={isHomeActive} onClick={() => navigate(HOME_PATH)}>
              {section.label}
            </NavigationItem>
          );
        return (
          <NavigationGroup key={section.slug} label={section.label} icon={section.icon} open={section.items?.some((item) => isItemActive(itemPath(section, item)))}>
            {section.items?.filter((item) => !item.hidden).map((item) => (
              <NavigationItem key={item.slug} active={isItemActive(itemPath(section, item))} onClick={() => navigate(itemPath(section, item))}>
                {item.label}
              </NavigationItem>
            ))}
          </NavigationGroup>
        );
      })}
    </Navigation>
  );

  return (
    <AppShell
      fillViewport
      logo="/humanforce-logo-yellow.svg"
      logoShort="/humanforce-monogram-yellow.svg"
      logoAlt="Humanforce"
      topLeft={<WorkspaceBadge label="Company Name" />}
      topRight={
        <>
          <IconButton icon="notifications" label="Notifications" variant="tertiary" size="small" />
          <IconButton icon="help" label="Help" variant="tertiary" size="small" />
          <DropdownMenu
            trigger={<IconButton icon="person" label="My account" variant="tertiary" size="small" />}
            items={[
              { label: 'My profile', icon: 'person' },
              { label: 'Settings', icon: 'settings' },
              { separator: true },
              { label: 'Sign out', icon: 'logout', variant: 'destructive', onClick: handleSignOut },
            ]}
          />
        </>
      }
      sidebar={sidebar}
    >
      <main>{children}</main>
    </AppShell>
  );
};
